import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Star } from "@/components/landing/Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getPosts } from "@/data/blogPosts";
import { useAiPosts } from "@/data/useAiPosts";
import { ShareButtons } from "@/components/ShareButtons";
import { NewsletterInline } from "@/components/landing/NewsletterInline";
import { useLang } from "@/i18n/LanguageContext";

const Divider = () => (
  <div className="flex items-center justify-center gap-3 py-2" aria-hidden="true">
    <span className="h-px w-16" style={{ background: "var(--line)" }} />
    <Star size={12} />
    <span className="h-px w-16" style={{ background: "var(--line)" }} />
  </div>
);

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const b = t.blog;
  const cat = (p) => (p.category === "B2B" ? b.catB2B : p.audience === "men" ? b.catMen : p.audience === "women" ? b.catWomen : b.catB2C);
  const { posts: aiPosts, loading: aiLoading } = useAiPosts(lang);
  const posts = [...aiPosts, ...getPosts(lang)];
  const post = posts.find((p) => p.slug === slug);
  const [progress, setProgress] = useState(0);
  const related = post
    ? [...posts.filter((p) => p.slug !== slug && p.category === post.category), ...posts.filter((p) => p.slug !== slug && p.category !== post.category)].slice(0, 2)
    : [];

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setProgress(max > 0 ? Math.min(100, (h.scrollTop / max) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!post) return;
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      document.title = post.metaTitle;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", post.metaDescription);
      const ogImage = post.isAi ? post.image : `https://mira-mirror.ai/og/${post.slug}.webp`;
      const og = {
        "og:type": "article",
        "og:url": `https://mira-mirror.ai/blog/${post.slug}`,
        "og:title": post.metaTitle,
        "og:description": post.metaDescription,
        "og:image": ogImage,
      };
      Object.entries(og).forEach(([k, v]) => document.querySelector(`meta[property="${k}"]`)?.setAttribute("content", v));
      const tw = { "twitter:title": post.metaTitle, "twitter:description": post.metaDescription, "twitter:image": ogImage };
      Object.entries(tw).forEach(([k, v]) => document.querySelector(`meta[name="${k}"]`)?.setAttribute("content", v));
    }, 0);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "post-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: post.title,
          description: post.metaDescription,
          image: post.image,
          datePublished: post.date,
          inLanguage: lang,
          author: { "@type": "Organization", name: "Mira Mirror" },
          publisher: { "@type": "Organization", name: "Mira Mirror", url: "https://mira-mirror.ai" },
          mainEntityOfPage: `https://mira-mirror.ai/blog/${post.slug}`,
        },
        {
          "@type": "FAQPage",
          mainEntity: post.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      ],
    });
    document.head.appendChild(ld);
    return () => {
      clearTimeout(timer);
      document.getElementById("post-ld")?.remove();
      document.title = t.seo.title;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", t.seo.description);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, lang, post?.metaTitle]);

  if (!post && aiLoading) return <main className="min-h-screen" style={{ background: "var(--surface)" }} data-testid="post-loading" />;
  if (!post) return <Navigate to="/blog" replace />;

  const fmt = new Date(post.date).toLocaleDateString(
    { de: "de-DE", en: "en-GB", es: "es-ES", it: "it-IT", fr: "fr-FR" }[lang] || "de-DE",
    { day: "numeric", month: "long", year: "numeric" }
  );
  const stripLinks = (t) => t.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  const linkCls = "underline decoration-[var(--gold)] underline-offset-4 transition-colors duration-300 hover:text-[var(--camel)]";
  const renderRich = (text) => {
    const re = /\[([^\]]+)\]\(([^)\s]+)\)/g;
    const parts = [];
    let last = 0, m, k = 0;
    while ((m = re.exec(text))) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      parts.push(m[2].startsWith("/")
        ? <Link key={`l${k++}`} to={m[2]} className={linkCls} style={{ color: "var(--on-surface)" }}>{m[1]}</Link>
        : <a key={`l${k++}`} href={m[2]} target="_blank" rel="noreferrer" className={linkCls} style={{ color: "var(--on-surface)" }}>{m[1]}</a>);
      last = re.lastIndex;
    }
    parts.push(text.slice(last));
    return parts;
  };
  const pullQuote = post.sections[2] ? stripLinks(post.sections[2].p).split(/(?<=[.!?])\s/)[0] : null;

  return (
    <main style={{ background: "var(--surface)", color: "var(--on-surface)" }} className="min-h-screen">
      <div className="fixed inset-x-0 top-0 z-[60] h-[3px]" style={{ background: "transparent" }} data-testid="reading-progress">
        <div className="h-full transition-[width] duration-150" style={{ width: `${progress}%`, background: "linear-gradient(90deg, var(--camel), var(--gold))" }} />
      </div>
      <Navbar />
      <article className="mx-auto max-w-3xl px-5 sm:px-8 pt-32 sm:pt-36 pb-24" data-testid="blog-post-page">
        <Link to="/blog" data-testid="post-back-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase transition-colors duration-300 hover:text-[var(--camel)]" style={{ color: "var(--muted-warm)", letterSpacing: "0.14em" }}>
          <ArrowLeft size={14} /> {b.back}
        </Link>

        <div className="mt-8 flex flex-wrap items-center gap-3 text-[11px] uppercase font-bold" style={{ letterSpacing: "0.12em" }}>
          <span className="-rotate-2 rounded-full px-3.5 py-1.5 text-white" style={{ background: "var(--camel)" }}>✦ {cat(post)}</span>
          <span style={{ color: "var(--muted-warm)" }}>{fmt}</span>
          <span className="rounded-full border px-3 py-1" style={{ borderColor: "var(--gold)", color: "var(--camel)" }}>{post.readTime} {b.min}</span>
        </div>

        <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.06] tracking-tight" data-testid="post-title">
          {post.title}
        </h1>
        <p className="mt-6 border-l-[3px] pl-5 font-serif-display text-lg sm:text-xl italic leading-relaxed" style={{ borderColor: "var(--gold)", color: "var(--muted-warm)" }}>
          {post.excerpt}
        </p>

        <div className="mt-10 overflow-hidden rounded-[2rem]">
          <img src={post.image} alt={post.title} className="w-full object-cover" style={{ maxHeight: 460 }} />
        </div>

        <div className="mt-14 space-y-12">
          {post.sections.map((s, i) => (
            <div key={i}>
              {i > 0 && <Divider />}
              {i === 2 && pullQuote && (
                <blockquote className="mb-12 border-l-[3px] py-2 pl-6" style={{ borderColor: "var(--gold)" }} data-testid="post-pullquote">
                  <p className="font-serif-display text-2xl sm:text-3xl italic leading-snug">«{pullQuote.replace(/[«»"]/g, "")}»</p>
                  <p className="mt-3 text-[11px] font-bold uppercase" style={{ letterSpacing: "0.18em", color: "var(--camel)" }}>— Mira Mirror {b.kicker}</p>
                </blockquote>
              )}
              <section data-testid={`post-section-${i + 1}`} className={i > 0 ? "mt-10" : ""}>
                <p className="text-[11px] font-black uppercase" style={{ letterSpacing: "0.22em", color: "var(--gold)" }}>0{i + 1}</p>
                <h2 className="mt-2 font-serif-display text-2xl sm:text-3xl font-semibold leading-snug">{s.h}</h2>
                {i === 0 ? (
                  <p className="mt-5 text-base sm:text-[1.0625rem] leading-[1.85]" style={{ color: "var(--muted-warm)" }}>
                    <span className="float-left select-none pr-3 font-serif-display text-6xl sm:text-7xl font-bold leading-[0.8]" style={{ color: "var(--camel)" }} data-testid="post-dropcap">
                      {s.p.charAt(0)}
                    </span>
                    {renderRich(s.p.slice(1))}
                  </p>
                ) : (
                  <p className="mt-4 text-base sm:text-[1.0625rem] leading-[1.85]" style={{ color: "var(--muted-warm)" }}>{renderRich(s.p)}</p>
                )}
              </section>
            </div>
          ))}
        </div>

        <section className="mt-16" data-testid="post-faq-section">
          <div className="flex items-center gap-2">
            <Star size={14} />
            <h2 className="font-serif-display text-3xl font-semibold">{b.faqTitle}</h2>
          </div>
          <Accordion type="single" collapsible className="mt-8 space-y-3">
            {post.faq.map((item, i) => (
              <AccordionItem key={i} value={`pf-${i}`} className="rounded-2xl border px-6" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid={`post-faq-item-${i + 1}`}>
                <AccordionTrigger className="py-5 text-left font-serif-display text-lg font-semibold hover:no-underline [&>svg]:text-[var(--camel)]">{item.q}</AccordionTrigger>
                <AccordionContent className="pb-6 text-sm sm:text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <ShareButtons url={`https://mira-mirror.ai/blog/${post.slug}`} title={post.title} image={post.isAi ? post.image : `https://mira-mirror.ai/pins/${post.slug}-${lang}.webp`} />

        <div className="mt-16 rounded-[2rem] border p-8 text-center" style={{ borderColor: "var(--gold)", background: "var(--surface-secondary)" }} data-testid="post-cta">
          <p className="font-serif-display text-2xl font-semibold">{b.ctaTitle}</p>
          <Link to="/" className="pill-btn mt-5 inline-flex rounded-full px-8 py-3 text-sm font-medium text-white" style={{ background: "var(--camel)" }} data-testid="post-cta-btn">
            {b.ctaBtn}
          </Link>
        </div>

        <section className="mt-16" data-testid="related-articles">
          <h2 className="font-serif-display text-3xl font-semibold">{b.relatedTitle}</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}`}
                data-testid={`related-card-${r.slug}`}
                className="group flex flex-col overflow-hidden rounded-[1.75rem] border transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(198,156,109,0.4)]"
                style={{ background: "var(--surface-secondary)", borderColor: "var(--line)" }}
              >
                <div className="h-36 overflow-hidden">
                  <img src={r.image} alt={r.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="w-fit -rotate-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase text-white" style={{ background: "var(--camel)", letterSpacing: "0.1em" }}>{cat(r)}</span>
                  <h3 className="mt-3 font-serif-display text-lg font-semibold leading-snug">{r.title}</h3>
                  <span className="mt-3 flex items-center gap-1.5 text-sm font-medium transition-transform duration-300 group-hover:translate-x-1" style={{ color: "var(--camel)" }}>
                    {b.readMore} <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <NewsletterInline />
      </article>
      <Footer />
    </main>
  );
}
